import { Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { animate, style, transition, trigger } from '@angular/animations';
import { driver, Driver, DriveStep, Side } from 'driver.js';
import { AuthService } from '@services/auth.service';
import { InventoryService } from '@services/user/inventory.service';
import { ToastService } from '@services/toast.service';
import { PageSelectorComponent, PriceHistoryChartComponent } from '@/app/shared';
import { CardComponent } from '@/app/shared/components/card/card.component';
import { CardRarity } from '@/app/shared/components/card/card.model';
import { User } from '@/app/core/models/user';
import { SellableInventoryCard } from '@/app/core/models/inventory.model';
import { CardListing, ListingSortBy, ListingSortDir, ListingStatus, MarketplaceSeller, SaleRecord } from '@/app/core/models/marketplace.model';
import { MarketplaceService } from '@services/card-game/marketplace.service';
import {Extension} from '@/app/core/models/collection/extension';
import {ExtensionService} from '@services/card-game/extension.service';

type MarketplaceTab = 'market' | 'mine' | 'history';

/** Option de tri combinée exposée dans l'UI — mappée en interne vers sortBy + sortDir. */
type MarketSortOption = 'recent' | 'price-asc' | 'price-desc' | 'rating' | 'name';

const SORT_OPTION_MAP: Record<MarketSortOption, { sortBy: ListingSortBy; sortDir: ListingSortDir | null }> = {
	'recent':     { sortBy: 'createdAt', sortDir: null },
	'price-asc':  { sortBy: 'price',     sortDir: 'asc' },
	'price-desc': { sortBy: 'price',     sortDir: 'desc' },
	'rating':     { sortBy: 'rating',    sortDir: null },
	'name':       { sortBy: 'name',      sortDir: null },
};

const PAGE_SIZE = 20;

@Component({
	selector: 'peps-marketplace',
	imports: [TranslatePipe, NgClass, RouterLink, CardComponent, PageSelectorComponent, PriceHistoryChartComponent],
	templateUrl: './marketplace.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('fadeInOut', [
			transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
			transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
		]),
		trigger('scaleInOut', [
			transition(':enter', [style({ transform: 'scale(0.92)', opacity: 0 }), animate('200ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))]),
			transition(':leave', [animate('200ms ease-in', style({ transform: 'scale(0.92)', opacity: 0 }))]),
		]),
	],
})
export class MarketplaceComponent implements OnInit, OnDestroy {

	private readonly marketplaceService = inject(MarketplaceService);
	private readonly inventoryService = inject(InventoryService);
	protected readonly authService = inject(AuthService);
	private readonly toastService = inject(ToastService);
	private readonly translate = inject(TranslateService);
	private readonly titleService = inject(Title);
	private readonly extensionService = inject(ExtensionService);

	isLoading = signal(true);
	isGridLoading = signal(false);
	isLoadingMore = signal(false);
	sellableCards = signal<SellableInventoryCard[]>([]);
	sellablePage = signal(0);
	sellableTotalPages = signal(1);
	isSellableLoading = signal(false);
	sellCardNameFilter = signal('');
	private readonly SELLABLE_PAGE_SIZE = 20;
	user = signal<User | null>(null);

	activeTab = signal<MarketplaceTab>('market');

	// ── Marché ──
	listings = signal<CardListing[]>([]);
	sortBy = signal<ListingSortBy>('createdAt');
	sortDir = signal<ListingSortDir | null>(null);
	sortOption = signal<MarketSortOption>('recent');
	rarityFilter = signal<CardRarity | null>(null);
	minPriceFilter = signal<number | null>(null);
	maxPriceFilter = signal<number | null>(null);
	sellerUsernameFilter = signal('');
	extensionFilter = signal('');
	/** Masque les annonces dont la carte est déjà possédée (peu importe le score) — pertinent seulement connecté. */
	hideOwnedFilter = signal(false);
	sellers = signal<MarketplaceSeller[]>([]);
	private marketPage = signal(0);
	marketHasMore = signal(false);
	private readonly debounceTimers: Partial<Record<string, ReturnType<typeof setTimeout>>> = {};

	/**
	 * Chargement des pages suivantes au scroll : un observer surveille la sentinelle
	 * en bas de grille. Le setter se redéclenche à chaque (dé)montage de l'élément
	 * (changement d'onglet, de filtre, rechargement de grille) et réattache l'observer.
	 */
	private scrollObserver: IntersectionObserver | null = null;
	private loadMoreEl: HTMLElement | null = null;

	@ViewChild('loadMoreAnchor')
	set loadMoreAnchor(ref: ElementRef<HTMLElement> | undefined) {
		this.scrollObserver?.disconnect();
		this.loadMoreEl = ref?.nativeElement ?? null;
		if (this.loadMoreEl) this.scrollObserver?.observe(this.loadMoreEl);
	}

	/**
	 * Après un append, la sentinelle peut rester dans le viewport sans nouvelle
	 * transition d'intersection : on relance l'observation pour forcer un check.
	 */
	private reobserveAnchor(): void {
		if (!this.scrollObserver || !this.loadMoreEl) return;
		this.scrollObserver.disconnect();
		this.scrollObserver.observe(this.loadMoreEl);
	}

	// ── Mes annonces ──
	myListings = signal<CardListing[]>([]);
	mineStatus = signal<ListingStatus>('ACTIVE');
	private minePage = signal(0);
	mineHasMore = signal(false);
	mineLoaded = false;

	// ── Historique de mes ventes (annonces au statut SOLD) ──
	mySales = signal<CardListing[]>([]);
	private salesPage = signal(0);
	salesHasMore = signal(false);
	salesLoaded = false;
	salesCount = computed(() => this.mySales().length);
	salesTotalEarned = computed(() => this.mySales().reduce((sum, l) => sum + l.price, 0));

	cardNameFilter = signal('');

	// ── Détail carte (achat + historique des prix) ──
	selectedListing = signal<CardListing | null>(null);
	isPurchasing = signal(false);
	cardSales = signal<SaleRecord[]>([]);
	isSalesLoading = signal(false);
	salesNotFound = signal(false);

	showSellModal = signal(false);
	selectedInventoryCard = signal<SellableInventoryCard | null>(null);
	listingPrice = signal(50);
	isListing = signal(false);

	// ── Édition du prix d'une annonce active ──
	editPriceListing = signal<CardListing | null>(null);
	editPriceValue = signal(50);
	isUpdatingPrice = signal(false);

	extensions = signal<Extension[]>([]);

	/** Tutoriel driver.js : carte d'exemple injectée pendant le tour, retirée dès qu'il se termine (quelle que soit l'issue). */
	private static readonly TUTORIAL_SEEN_KEY = 'marketplace-tutorial-seen';
	private static readonly DEMO_LISTING_ID = 'tutorial-demo-listing';
	private static readonly DEMO_CARD_ID = 'tutorial-demo-card';
	private driverObj: Driver | null = null;
	demoListing = signal<CardListing | null>(null);

	readonly rarityOptions: { key: CardRarity; labelKey: string; color: string; bg: string }[] = [
		{ key: 'COMMON',    labelKey: 'page.marketplace.rarity-common',    color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
		{ key: 'RARE',      labelKey: 'page.marketplace.rarity-rare',      color: '#38bdf8', bg: 'rgba(56,189,248,0.12)'  },
		{ key: 'EPIC',      labelKey: 'page.marketplace.rarity-epic',      color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)'  },
		{ key: 'LEGENDARY', labelKey: 'page.marketplace.rarity-legendary', color: '#eab308', bg: 'rgba(234,179,8,0.12)'   },
		{ key: 'COLLECTOR', labelKey: 'page.marketplace.rarity-collector', color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
	];

	readonly mineStatusOptions: { key: ListingStatus; labelKey: string }[] = [
		{ key: 'ACTIVE',    labelKey: 'page.marketplace.mine.status-active' },
		{ key: 'CANCELLED', labelKey: 'page.marketplace.mine.status-cancelled' },
	];

	visibleListings = computed(() => {
		switch (this.activeTab()) {
			case 'mine':    return this.myListings();
			case 'history': return this.mySales();
			default:        return this.listings();
		}
	});

	/**
	 * `market` : le nom est filtré côté serveur (`cardName`), la liste reçue est déjà bonne.
	 * `mine` : pas de filtre `cardName` côté back sur `/listings/mine`, on filtre en mémoire comme avant.
	 */
	filteredListings = computed(() => {
		if (this.activeTab() === 'market') {
			const demo = this.demoListing();
			return demo ? [demo, ...this.listings()] : this.listings();
		}
		const source = this.activeTab() === 'history' ? this.mySales() : this.myListings();
		const name = this.cardNameFilter().toLowerCase().trim();
		if (!name) return source;
		return source.filter(l => l.card.name.toLowerCase().includes(name));
	});

	hasMore = computed(() => {
		switch (this.activeTab()) {
			case 'mine':    return this.mineHasMore();
			case 'history': return this.salesHasMore();
			default:        return this.marketHasMore();
		}
	});

	rarityColor(rarity: CardRarity): string {
		return this.rarityOptions.find(r => r.key === rarity)?.color ?? '#9ca3af';
	}

	ngOnInit(): void {
		this.titleService.setTitle('Team Peps - ' + this.translate.instant('page.marketplace.title'));
		window.scrollTo({ top: 0, behavior: 'smooth' });
		this.extensionService.getAllExtensions().subscribe({
			next: (extensions) => this.extensions.set(extensions),
			error: () => {},
		});
		this.fetchListings(0, false);
		this.marketplaceService.getSellers().subscribe({
			next: (sellers) => this.sellers.set(sellers),
			error: () => {},
		});
		if (this.authService.isAuthenticated()) {
			this.refreshUserAndInventory();
		}
		this.maybeAutoStartTour();

		if (typeof IntersectionObserver !== 'undefined') {
			this.scrollObserver = new IntersectionObserver(
				(entries) => {
					if (entries.some(e => e.isIntersecting)) this.onScrollNearEnd();
				},
				{ rootMargin: '600px 0px' },
			);
		}
	}

	ngOnDestroy(): void {
		// Sécurité : si le composant est détruit modal ouverte, on ne laisse pas le scroll bloqué.
		document.body.style.overflow = '';
		Object.values(this.debounceTimers).forEach(t => t && clearTimeout(t));
		this.driverObj?.destroy();
		this.scrollObserver?.disconnect();
	}

	/** Déclenché quand la sentinelle bas de grille entre dans le viewport (marge 600px). */
	private onScrollNearEnd(): void {
		if (!this.hasMore() || this.isLoadingMore() || this.isGridLoading()) return;
		this.loadMore();
	}

	/** Debounce générique (une clé = un timer indépendant) pour les filtres texte/nombre avant appel réseau. */
	private debounce(key: string, fn: () => void, delay = 300): void {
		const existing = this.debounceTimers[key];
		if (existing) clearTimeout(existing);
		this.debounceTimers[key] = setTimeout(fn, delay);
	}

	private refreshUserAndInventory(): void {
		// loadUser() met à jour le signal partagé de l'AuthService (solde PepsCoins
		// affiché ailleurs) en plus du signal local.
		this.authService.loadUser().subscribe({
			next: (user) => this.user.set(user),
			error: () => {},
		});
		this.refreshSellableCards();
	}

	private refreshSellableCards(page = 0): void {
		this.isSellableLoading.set(true);
		this.inventoryService.getSellableInventory({
			page,
			size: this.SELLABLE_PAGE_SIZE,
			sortBy: 'score',
			extensionId: this.extensionFilter() || undefined,
			name: this.sellCardNameFilter().trim() || undefined,
		}).subscribe({
			next: (res) => {
				this.sellableCards.set(res.content);
				this.sellablePage.set(res.number);
				this.sellableTotalPages.set(res.totalPages);
				this.isSellableLoading.set(false);
			},
			error: () => {
				this.isSellableLoading.set(false);
			},
		});
	}

	onSellablePageChange(page: number): void {
		this.refreshSellableCards(page);
	}

	onSellCardNameInput(event: Event): void {
		this.sellCardNameFilter.set((event.target as HTMLInputElement).value);
		this.debounce('sellCardName', () => this.refreshSellableCards(0));
	}

	private fetchListings(page: number, append: boolean, silent = false): void {
		if (append) this.isLoadingMore.set(true);
		else if (!silent) this.isGridLoading.set(true);

		this.marketplaceService.getListings({
			page,
			size: PAGE_SIZE,
			sortBy: this.sortBy(),
			sortDir: this.sortDir() ?? undefined,
			rarity: this.rarityFilter() ?? undefined,
			cardName: this.cardNameFilter().trim() || undefined,
			minPrice: this.minPriceFilter() ?? undefined,
			maxPrice: this.maxPriceFilter() ?? undefined,
			sellerUsername: this.sellerUsernameFilter().trim() || undefined,
			extensionId: this.extensionFilter() || undefined,
			hideOwned: this.hideOwnedFilter() || undefined,
		}).subscribe({
			next: (res) => {
				this.listings.set(append ? [...this.listings(), ...res.content] : res.content);
				this.marketPage.set(res.number);
				this.marketHasMore.set(res.number + 1 < res.totalPages);
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
				if (append) setTimeout(() => this.reobserveAnchor());
			},
			error: () => {
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
			},
		});
	}

	private fetchMyListings(page: number, append: boolean, silent = false): void {
		if (append) this.isLoadingMore.set(true);
		else if (!silent) this.isGridLoading.set(true);

		this.marketplaceService.getMyListings({
			status: this.mineStatus(),
			page,
			size: PAGE_SIZE,
		}).subscribe({
			next: (res) => {
				this.myListings.set(append ? [...this.myListings(), ...res.content] : res.content);
				this.minePage.set(res.number);
				this.mineHasMore.set(res.number + 1 < res.totalPages);
				this.mineLoaded = true;
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
				if (append) setTimeout(() => this.reobserveAnchor());
			},
			error: () => {
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
			},
		});
	}

	private fetchMySales(page: number, append: boolean, silent = false): void {
		if (append) this.isLoadingMore.set(true);
		else if (!silent) this.isGridLoading.set(true);

		this.marketplaceService.getMyListings({
			status: 'SOLD',
			page,
			size: PAGE_SIZE,
		}).subscribe({
			next: (res) => {
				this.mySales.set(append ? [...this.mySales(), ...res.content] : res.content);
				this.salesPage.set(res.number);
				this.salesHasMore.set(res.number + 1 < res.totalPages);
				this.salesLoaded = true;
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
				if (append) setTimeout(() => this.reobserveAnchor());
			},
			error: () => {
				this.isLoading.set(false);
				this.isGridLoading.set(false);
				this.isLoadingMore.set(false);
			},
		});
	}

	switchTab(tab: MarketplaceTab): void {
		if (this.activeTab() === tab) return;
		this.activeTab.set(tab);
		if (tab === 'mine' && !this.mineLoaded) this.fetchMyListings(0, false, true);
		if (tab === 'history' && !this.salesLoaded) this.fetchMySales(0, false, true);
		// Le champ recherche est partagé entre les deux onglets mais ne déclenche
		// un appel serveur que pour 'market' (voir onCardNameInput) : en revenant
		// sur cet onglet, on s'assure que la liste reflète bien le filtre courant.
		if (tab === 'market') this.fetchListings(0, false, true);
	}

	setSortOption(option: MarketSortOption): void {
		if (this.sortOption() === option) return;
		this.sortOption.set(option);
		const { sortBy, sortDir } = SORT_OPTION_MAP[option];
		this.sortBy.set(sortBy);
		this.sortDir.set(sortDir);
		this.fetchListings(0, false, true);
	}

	setRarityFilter(rarity: CardRarity | null): void {
		const next = this.rarityFilter() === rarity ? null : rarity;
		if (this.rarityFilter() === next) return;
		this.rarityFilter.set(next);
		this.fetchListings(0, false, true);
	}

	toggleHideOwnedFilter(): void {
		this.hideOwnedFilter.set(!this.hideOwnedFilter());
		this.fetchListings(0, false, true);
	}

	setMineStatus(status: ListingStatus): void {
		if (this.mineStatus() === status) return;
		this.mineStatus.set(status);
		this.fetchMyListings(0, false, true);
	}

	onCardNameInput(event: Event): void {
		this.cardNameFilter.set((event.target as HTMLInputElement).value);
		if (this.activeTab() !== 'market') return; // 'mine' est filtré en mémoire, pas de round-trip nécessaire
		this.debounce('cardName', () => this.fetchListings(0, false, true));
	}

	/** Sélection exacte dans le dropdown (pas de saisie libre) : pas besoin de debounce. */
	setSellerFilter(username: string): void {
		if (this.sellerUsernameFilter() === username) return;
		this.sellerUsernameFilter.set(username);
		this.fetchListings(0, false, true);
	}

	setExtensionFilter(extensionId: string): void {
		if (this.extensionFilter() === extensionId) return;
		this.extensionFilter.set(extensionId);
		this.fetchListings(0, false, true);
	}

	onMinPriceInput(event: Event): void {
		const raw = (event.target as HTMLInputElement).value;
		this.minPriceFilter.set(raw === '' ? null : Number(raw));
		this.debounce('minPrice', () => this.fetchListings(0, false, true));
	}

	onMaxPriceInput(event: Event): void {
		const raw = (event.target as HTMLInputElement).value;
		this.maxPriceFilter.set(raw === '' ? null : Number(raw));
		this.debounce('maxPrice', () => this.fetchListings(0, false, true));
	}

	loadMore(): void {
		if (this.activeTab() === 'mine') {
			this.fetchMyListings(this.minePage() + 1, true);
		} else if (this.activeTab() === 'history') {
			this.fetchMySales(this.salesPage() + 1, true);
		} else {
			this.fetchListings(this.marketPage() + 1, true);
		}
	}

	private removeListing(id: string): void {
		this.listings.set(this.listings().filter(l => l.id !== id));
		this.myListings.set(this.myListings().filter(l => l.id !== id));
	}

	/**
	 * Re-synchronise le marché avec le serveur après un achat (ou une annonce devenue
	 * obsolète). Le retrait optimiste via removeListing() donne le retour immédiat ;
	 * ce re-fetch recale la liste sur l'état réel (page complétée à nouveau, annonces
	 * des autres joueurs, dropdown vendeurs à jour).
	 */
	private refreshMarketAfterPurchase(): void {
		this.fetchListings(0, false, true);
		if (this.mineLoaded) this.fetchMyListings(0, false, true);
		this.marketplaceService.getSellers().subscribe({
			next: (sellers) => this.sellers.set(sellers),
			error: () => {},
		});
	}

	private showApiError(err: unknown, fallbackKey: string): void {
		const message = (err as HttpErrorResponse)?.error?.message;
		this.toastService.show(message || this.translate.instant(fallbackKey), 'error', 5000);
	}

	isOwnListing(listing: CardListing): boolean {
		return listing.seller.username === this.user()?.username;
	}

	canAfford(listing: CardListing): boolean {
		const coins = this.user()?.coins;
		return coins !== undefined && coins >= listing.price;
	}

	/** Ouvre la modale détail (carte en grand + historique des prix). L'achat ne se fait plus que depuis là. */
	openCardDetail(listing: CardListing): void {
		this.selectedListing.set(listing);
		document.body.style.overflow = 'hidden';
		this.loadSales(listing.card.id);
	}

	closeCardDetail(): void {
		if (this.isPurchasing()) return;
		this.selectedListing.set(null);
		document.body.style.overflow = '';
	}

	onCardDetailBackdrop(event: Event): void {
		if (event.target === event.currentTarget) this.closeCardDetail();
	}

	private loadSales(cardId: string): void {
		if (cardId === MarketplaceComponent.DEMO_CARD_ID) {
			this.cardSales.set(this.buildDemoSales());
			this.salesNotFound.set(false);
			this.isSalesLoading.set(false);
			return;
		}
		this.cardSales.set([]);
		this.salesNotFound.set(false);
		this.isSalesLoading.set(true);
		this.marketplaceService.getSales(cardId, 20).subscribe({
			next: (sales) => {
				// API renvoie DESC (plus récente en premier) ; le graphe veut l'ordre chronologique.
				this.cardSales.set(sales.slice().reverse());
				this.isSalesLoading.set(false);
			},
			error: (err: HttpErrorResponse) => {
				this.isSalesLoading.set(false);
				if (err.status === 404) this.salesNotFound.set(true);
			},
		});
	}

	confirmBuy(): void {
		const listing = this.selectedListing();
		if (!listing || this.isPurchasing()) return;
		if (this.isDemoListing(listing)) {
			this.toastService.show(this.translate.instant('page.marketplace.tutorial.demo-buy-disabled'), 'info', 4000);
			return;
		}
		this.isPurchasing.set(true);
		this.marketplaceService.buyListing(listing.id).subscribe({
			next: () => {
				this.toastService.show(this.translate.instant('page.marketplace.buy-modal.success'), 'success', 5000);
				this.isPurchasing.set(false);
				this.selectedListing.set(null);
				document.body.style.overflow = '';
				this.removeListing(listing.id);
				this.refreshUserAndInventory();
				this.refreshMarketAfterPurchase();
			},
			error: (err: HttpErrorResponse) => {
				this.isPurchasing.set(false);
				this.showApiError(err, 'page.marketplace.buy-modal.error');
				if (err.status === 404 || err.status === 409) {
					this.removeListing(listing.id);
					this.selectedListing.set(null);
					document.body.style.overflow = '';
					this.refreshMarketAfterPurchase();
				}
			},
		});
	}

	cancelListing(listing: CardListing): void {
		this.marketplaceService.cancelListing(listing.id).subscribe({
			next: () => {
				this.toastService.show(this.translate.instant('page.marketplace.listing.cancel-success'), 'success', 5000);
				this.removeListing(listing.id);
				this.refreshSellableCards();
			},
			error: (err: HttpErrorResponse) => this.showApiError(err, 'page.marketplace.listing.cancel-error'),
		});
	}

	openEditPriceModal(listing: CardListing): void {
		this.editPriceListing.set(listing);
		this.editPriceValue.set(listing.price);
		this.isUpdatingPrice.set(false);
		document.body.style.overflow = 'hidden';
	}

	closeEditPriceModal(): void {
		if (this.isUpdatingPrice()) return;
		this.editPriceListing.set(null);
		document.body.style.overflow = '';
	}

	onEditPriceBackdrop(event: Event): void {
		if (event.target === event.currentTarget) this.closeEditPriceModal();
	}

	onEditPriceInput(event: Event): void {
		const raw = (event.target as HTMLInputElement).value;
		this.editPriceValue.set(raw === '' ? 0 : Math.floor(Number(raw)));
	}

	confirmEditPrice(): void {
		const listing = this.editPriceListing();
		if (!listing || this.isUpdatingPrice()) return;
		const price = this.editPriceValue();
		if (!Number.isFinite(price) || price <= 0) {
			this.toastService.show(this.translate.instant('page.marketplace.edit-price-modal.invalid'), 'error', 4000);
			return;
		}
		if (price === listing.price) {
			this.closeEditPriceModal();
			return;
		}
		this.isUpdatingPrice.set(true);
		this.marketplaceService.updateListingPrice(listing.id, price).subscribe({
			next: (updated) => {
				this.applyListingPrice(updated.id, updated.price);
				this.isUpdatingPrice.set(false);
				this.editPriceListing.set(null);
				document.body.style.overflow = '';
				this.toastService.show(this.translate.instant('page.marketplace.edit-price-modal.success'), 'success', 5000);
			},
			error: (err: HttpErrorResponse) => {
				this.isUpdatingPrice.set(false);
				this.showApiError(err, 'page.marketplace.edit-price-modal.error');
				if (err.status === 404 || err.status === 409) {
					this.removeListing(listing.id);
					this.editPriceListing.set(null);
					document.body.style.overflow = '';
				}
			},
		});
	}

	private applyListingPrice(id: string, price: number): void {
		const patch = (l: CardListing): CardListing => l.id === id ? { ...l, price } : l;
		this.listings.set(this.listings().map(patch));
		this.myListings.set(this.myListings().map(patch));
	}

	openSellModal(): void {
		this.selectedInventoryCard.set(null);
		this.listingPrice.set(50);
		this.sellCardNameFilter.set('');
		this.showSellModal.set(true);
		this.refreshSellableCards(0);
		document.body.style.overflow = 'hidden';
	}

	closeSellModal(): void {
		if (this.isListing()) return;
		this.showSellModal.set(false);
		document.body.style.overflow = '';
	}

	onSellModalBackdrop(event: Event): void {
		if (event.target === event.currentTarget) this.closeSellModal();
	}

	selectInventoryCard(entry: SellableInventoryCard): void {
		this.selectedInventoryCard.set(entry);
	}

	confirmSell(): void {
		const entry = this.selectedInventoryCard();
		if (!entry || this.isListing()) return;
		this.isListing.set(true);
		this.marketplaceService.createListing({ userCardId: entry.userCardId, price: this.listingPrice() }).subscribe({
			next: () => {
				this.toastService.show(this.translate.instant('page.marketplace.sell-modal.success'), 'success', 5000);
				this.isListing.set(false);
				this.showSellModal.set(false);
				document.body.style.overflow = '';
				if (this.activeTab() === 'market') this.fetchListings(0, false, true);
				this.mineLoaded = false;
				if (this.activeTab() === 'mine') this.fetchMyListings(0, false, true);
				this.refreshSellableCards();
			},
			error: (err: HttpErrorResponse) => {
				this.isListing.set(false);
				this.showApiError(err, 'page.marketplace.sell-modal.error');
			},
		});
	}

	isDemoListing(listing: CardListing): boolean {
		return listing.id === MarketplaceComponent.DEMO_LISTING_ID;
	}

	/** Carte fictive affichée pendant le tutoriel — reprend l'image d'une vraie carte déjà chargée si possible, sinon reste sans visuel. */
	private buildDemoListing(): CardListing {
		const sampleCard = this.listings()[0]?.card;
		return {
			id: MarketplaceComponent.DEMO_LISTING_ID,
			card: {
				id: MarketplaceComponent.DEMO_CARD_ID,
				rarity: sampleCard?.rarity ?? 'LEGENDARY',
				name: this.translate.instant('page.marketplace.tutorial.demo-card-name'),
				rating: sampleCard?.rating ?? 4,
				fileName: sampleCard?.fileName ?? '',
				extension: sampleCard?.extension ?? 'demo',
				author: sampleCard?.author ?? 'TeamPeps',
			},
			score: 92,
			seller: {
				id: 'tutorial-demo-seller',
				username: 'TeamPeps',
				picture: this.user()?.picture ?? '',
			},
			price: 250,
			status: 'ACTIVE',
			createdAt: new Date().toISOString(),
			soldAt: null,
		};
	}

	/** Historique de ventes fictif pour que le graphe du détail ne soit pas vide sur la carte d'exemple. */
	private buildDemoSales(): SaleRecord[] {
		const now = Date.now();
		const day = 24 * 60 * 60 * 1000;
		return [180, 210, 195, 230, 250].map((price, i) => ({
			price,
			score: 92,
			soldAt: new Date(now - (5 - i) * day).toISOString(),
		}));
	}

	/** Lancé une seule fois automatiquement (flag localStorage) — rejouable via le bouton "?" du header. */
	private maybeAutoStartTour(): void {
		// `localStorage` n'existe pas côté serveur (SSR) : ngOnInit s'exécute
		// aussi pendant le rendu serveur, sans ce garde-fou l'accès y lève une
		// ReferenceError et fait échouer le rendu de la page.
		try {
			if (localStorage.getItem(MarketplaceComponent.TUTORIAL_SEEN_KEY)) return;
		} catch {
			return;
		}
		setTimeout(() => this.startTour(), 500);
	}

	startTour(): void {
		localStorage.setItem(MarketplaceComponent.TUTORIAL_SEEN_KEY, 'true');
		this.demoListing.set(this.buildDemoListing());

		this.driverObj = driver({
			showProgress: true,
			smoothScroll: true,
			overlayColor: 'rgba(19,20,21,0.75)',
			popoverClass: 'peps-driver-popover',
			progressText: '{{current}} / {{total}}',
			nextBtnText: this.translate.instant('page.marketplace.tutorial.next'),
			prevBtnText: this.translate.instant('page.marketplace.tutorial.prev'),
			doneBtnText: this.translate.instant('page.marketplace.tutorial.done'),
			// Quelle que soit l'issue (Terminé, croix, Échap, clic sur l'overlay) : on retire la carte d'exemple.
			onDestroyed: () => this.demoListing.set(null),
			steps: this.buildTourSteps(),
		});
		this.driverObj.drive();
	}

	private buildTourSteps(): DriveStep[] {
		const step = (selector: string, key: string, side: Side = 'bottom'): DriveStep => ({
			element: selector,
			skipMissingElement: true,
			popover: {
				side,
				align: 'start',
				title: this.translate.instant(`page.marketplace.tutorial.${key}.title`),
				description: this.translate.instant(`page.marketplace.tutorial.${key}.text`),
			},
		});

		const steps: DriveStep[] = [step('[data-tour="header"]', 'header')];

		if (this.authService.isAuthenticated()) {
			steps.push(
				step('[data-tour="balance"]', 'balance'),
				step('[data-tour="sell"]', 'sell'),
				step('[data-tour="tabs"]', 'tabs'),
			);
		}

		steps.push(
			step('[data-tour="search"]', 'search'),
			step('[data-tour="sort"]', 'sort'),
			step('[data-tour="rarity-filter"]', 'rarity-filter'),
			step('[data-tour="demo-card"]', 'demo-card', 'top'),
		);

		return steps;
	}
}
